import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"

import AddRoundedIcon from "@mui/icons-material/AddRounded"
import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined"
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined"
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded"
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded"
import SearchRoundedIcon from "@mui/icons-material/SearchRounded"
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded"
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined"

import MainLayout from "../../layouts/MainLayout"

import {
  cancelProductionOrder,
  completeProductionOrder,
  createProductionOrder,
  getProductionBomById,
  getProductionOrderById,
  getProductionOrderFormData,
  getProductionOrders,
  normalizeProductionArray,
  reserveProductionMaterials,
  startProductionOrder,
} from "../../services/productionService"


/* =========================================================
   CONSTANTES
   ========================================================= */

const EMPTY_FORM = {
  bom_id: "",
  warehouse_id: "",
  planned_quantity: 1,
  planned_start_date: "",
  planned_end_date: "",
  notes: "",
}

const EMPTY_COMPLETION_FORM = {
  produced_quantity: "",
  rejected_quantity: 0,
  notes: "",
}


/* =========================================================
   HELPERS GÉNÉRAUX
   ========================================================= */

function toNumber(value, fallback = 0) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return fallback
  }

  const normalized =
    typeof value === "string"
      ? value.replace(",", ".")
      : value

  const number = Number(normalized)

  return Number.isFinite(number)
    ? number
    : fallback
}


function positiveNumber(value, fallback = 0) {
  const number = toNumber(value, fallback)

  return number > 0
    ? number
    : fallback
}


function normalizeStatus(value = "") {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replaceAll("-", "_")
    .replaceAll(" ", "_")
}


function formatQuantity(value, unit = "") {
  const number = toNumber(value)

  const formatted =
    number.toLocaleString("fr-FR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    })

  return unit
    ? `${formatted} ${unit}`
    : formatted
}


function formatDate(value) {
  if (!value) {
    return "-"
  }

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return String(value)
  }

  return date.toLocaleDateString("fr-FR")
}


function formatDateTime(value) {
  if (!value) {
    return "-"
  }

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return String(value)
  }

  return date.toLocaleString("fr-FR")
}


function extractError(error) {
  const detail =
    error?.response?.data?.detail

  if (typeof detail === "string") {
    return detail
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        const location =
          Array.isArray(item?.loc)
            ? item.loc
                .filter(
                  (part) =>
                    part !== "body"
                )
                .join(" → ")
            : ""

        const message =
          item?.msg ||
          "Erreur de validation"

        return location
          ? `${location} : ${message}`
          : message
      })
      .join(" • ")
  }

  if (
    detail &&
    typeof detail === "object"
  ) {
    return (
      detail.message ||
      detail.error ||
      JSON.stringify(detail)
    )
  }

  const data =
    error?.response?.data

  if (typeof data === "string") {
    return data
  }

  return (
    data?.message ||
    error?.message ||
    "Une erreur est survenue."
  )
}


function getOrderWorkflowState(order = {}) {
  const explicitState =
    normalizeStatus(
      order.workflow_state ||
      order.reservation_state ||
      ""
    )

  if (explicitState) {
    return explicitState === "RESERVED"
      ? "MATERIALS_RESERVED"
      : explicitState
  }

  const status =
    normalizeStatus(order.status)

  if (
    status === "PLANNED" ||
    order.materials_reserved === true
  ) {
    return "MATERIALS_RESERVED"
  }

  if (status === "DRAFT") {
    return "TO_PREPARE"
  }

  if (
    [
      "IN_PROGRESS",
      "IN_PRODUCTION",
      "STARTED",
    ].includes(status)
  ) {
    return "IN_PRODUCTION"
  }

  if (
    [
      "COMPLETED",
      "FINISHED",
      "DONE",
    ].includes(status)
  ) {
    return "COMPLETED"
  }

  if (
    [
      "CANCELLED",
      "CANCELED",
    ].includes(status)
  ) {
    return "CANCELLED"
  }

  return status || "TO_PREPARE"
}


function statusInfo(value, order = null) {
  const status =
    order
      ? getOrderWorkflowState(order)
      : normalizeStatus(value)

  const map = {
    TO_PREPARE: {
      label: "À préparer",
      color: "default",
    },

    DRAFT: {
      label: "À préparer",
      color: "default",
    },

    PLANNED: {
      label: "Matières réservées",
      color: "warning",
    },

    RESERVED: {
      label: "Matières réservées",
      color: "warning",
    },

    MATERIALS_RESERVED: {
      label: "Matières réservées",
      color: "warning",
    },

    IN_PROGRESS: {
      label: "En production",
      color: "primary",
    },

    IN_PRODUCTION: {
      label: "En production",
      color: "primary",
    },

    STARTED: {
      label: "En production",
      color: "primary",
    },

    COMPLETED: {
      label: "Terminé",
      color: "success",
    },

    FINISHED: {
      label: "Terminé",
      color: "success",
    },

    DONE: {
      label: "Terminé",
      color: "success",
    },

    CANCELLED: {
      label: "Annulé",
      color: "error",
    },

    CANCELED: {
      label: "Annulé",
      color: "error",
    },
  }

  return (
    map[status] || {
      label:
        status ||
        "À préparer",
      color: "default",
    }
  )
}


/* =========================================================
   HELPERS BOM
   ========================================================= */

function getBomOutputQuantity(bom = {}) {
  return positiveNumber(
    bom.output_quantity ??
      bom.quantity ??
      bom.base_quantity ??
      bom.production_quantity ??
      bom.finished_quantity,
    1
  )
}


function getBomOutputUnit(bom = {}) {
  return (
    bom.output_unit ||
    bom.unit ||
    bom.finished_product_unit ||
    bom.product_unit ||
    ""
  )
}


function getBomProductName(bom = {}) {
  return (
    bom.finished_product_name ||
    bom.product_name ||
    bom.name ||
    ""
  )
}


function getBomProductCode(bom = {}) {
  return (
    bom.finished_product_code ||
    bom.product_code ||
    ""
  )
}


function bomLabel(bom = {}) {
  const code =
    bom.code ||
    `BOM-${bom.id}`

  const product = [
    getBomProductCode(bom),
    getBomProductName(bom),
  ]
    .filter(Boolean)
    .join(" - ")

  const version =
    bom.version
      ? ` • v${bom.version}`
      : ""

  return product
    ? `${code} • ${product}${version}`
    : `${code}${version}`
}


function getBomItems(bom = {}) {
  const candidates = [
    bom.items,
    bom.components,
    bom.materials,
    bom.bom_items,
    bom.lines,
  ]

  for (const candidate of candidates) {
    const list =
      normalizeProductionArray(candidate)

    if (list.length > 0) {
      return list
    }
  }

  return []
}


/* =========================================================
   HELPERS ENTREPÔT
   ========================================================= */

function warehouseLabel(warehouse = {}) {
  const code =
    warehouse.code ||
    warehouse.warehouse_code ||
    ""

  const name =
    warehouse.name ||
    warehouse.warehouse_name ||
    ""

  const type =
    warehouse.type_label ||
    warehouse.warehouse_type_label ||
    warehouse.warehouse_type ||
    warehouse.type ||
    ""

  const main = [
    code,
    name,
  ]
    .filter(Boolean)
    .join(" - ")

  if (main && type) {
    return `${main} • ${type}`
  }

  return (
    main ||
    `Entrepôt #${warehouse.id}`
  )
}


/* =========================================================
   HELPERS ORDRE
   ========================================================= */

function getOrderCode(order = {}) {
  return (
    order.code ||
    order.order_number ||
    order.production_order_number ||
    `OP-${String(
      order.id || ""
    ).padStart(5, "0")}`
  )
}


function getOrderProductName(order = {}) {
  return (
    order.finished_product_name ||
    order.product_name ||
    order.output_product_name ||
    "-"
  )
}


function getOrderProductCode(order = {}) {
  return (
    order.finished_product_code ||
    order.product_code ||
    order.output_product_code ||
    ""
  )
}


function getOrderQuantity(order = {}) {
  return (
    order.planned_quantity ??
    order.quantity ??
    order.target_quantity ??
    order.output_quantity ??
    0
  )
}


function getOrderUnit(order = {}) {
  return (
    order.unit ||
    order.output_unit ||
    order.finished_product_unit ||
    ""
  )
}


function getOrderWarehouseName(order = {}) {
  return [
    order.warehouse_code,
    order.warehouse_name,
  ]
    .filter(Boolean)
    .join(" - ")
}


/* =========================================================
   PAGE
   ========================================================= */

export default function ProductionOrdersPage() {
  const [
    loading,
    setLoading,
  ] = useState(true)

  const [
    saving,
    setSaving,
  ] = useState(false)

  const [
    detailLoading,
    setDetailLoading,
  ] = useState(false)

  const [
    orders,
    setOrders,
  ] = useState([])

  const [
    boms,
    setBoms,
  ] = useState([])

  const [
    bomDetails,
    setBomDetails,
  ] = useState({})

  const [
    bomDetailLoading,
    setBomDetailLoading,
  ] = useState(false)

  const [
    warehouses,
    setWarehouses,
  ] = useState([])

  const [
    search,
    setSearch,
  ] = useState("")

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("ALL")

  const [
    createOpen,
    setCreateOpen,
  ] = useState(false)

  const [
    detailOpen,
    setDetailOpen,
  ] = useState(false)

  const [
    completeOpen,
    setCompleteOpen,
  ] = useState(false)

  const [
    selectedOrder,
    setSelectedOrder,
  ] = useState(null)

  const [
    form,
    setForm,
  ] = useState({
    ...EMPTY_FORM,
  })

  const [
    completionForm,
    setCompletionForm,
  ] = useState({
    ...EMPTY_COMPLETION_FORM,
  })

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  /* =======================================================
     NOTIFICATION
     ======================================================= */

  const notify = useCallback(
    (
      message,
      severity = "success"
    ) => {
      setSnackbar({
        open: true,
        severity,
        message,
      })
    },
    []
  )


  /* =======================================================
     CHARGEMENT
     ======================================================= */

  const loadData =
    useCallback(async () => {
      setLoading(true)

      try {
        const [
          orderResponse,
          formDataResponse,
        ] =
          await Promise.all([
            getProductionOrders(),
            getProductionOrderFormData(),
          ])

        const orderList =
          normalizeProductionArray(
            orderResponse,
            "orders"
          )

        let bomList =
          normalizeProductionArray(
            formDataResponse?.boms,
            "boms"
          )

        if (
          bomList.length === 0
        ) {
          bomList =
            normalizeProductionArray(
              formDataResponse,
              "boms"
            )
        }

        let warehouseList =
          normalizeProductionArray(
            formDataResponse?.warehouses,
            "warehouses"
          )

        if (
          warehouseList.length ===
          0
        ) {
          warehouseList =
            normalizeProductionArray(
              formDataResponse,
              "warehouses"
            )
        }

        setOrders(orderList)

        setBoms(
          bomList.filter(
            (bom) =>
              bom.is_active !== false &&
              normalizeStatus(
                bom.status
              ) !== "INACTIVE"
          )
        )

        setWarehouses(
          warehouseList.filter(
            (warehouse) =>
              warehouse.is_active !==
              false
          )
        )
      } catch (error) {
        notify(
          extractError(error),
          "error"
        )
      } finally {
        setLoading(false)
      }
    }, [notify])


  useEffect(() => {
    loadData()
  }, [loadData])


  /* =======================================================
     BOM SÉLECTIONNÉE
     ======================================================= */

  const selectedBomHeader =
    useMemo(() => {
      if (!form.bom_id) {
        return null
      }

      return (
        boms.find(
          (bom) =>
            Number(bom.id) ===
            Number(form.bom_id)
        ) || null
      )
    }, [
      boms,
      form.bom_id,
    ])


  /*
   * Le backend mis à jour renvoie déjà `items`
   * dans /production/orders/form-data.
   *
   * Ce fallback garde néanmoins la page robuste :
   * si un ancien backend renvoie seulement l'en-tête,
   * on charge automatiquement le détail de la BOM.
   */
  useEffect(() => {
    let active = true

    const loadSelectedBomDetail =
      async () => {
        if (!form.bom_id) {
          return
        }

        const currentBom =
          boms.find(
            (bom) =>
              Number(bom.id) ===
              Number(form.bom_id)
          )

        if (!currentBom) {
          return
        }

        if (
          getBomItems(currentBom).length > 0 ||
          bomDetails[
            String(form.bom_id)
          ]
        ) {
          return
        }

        try {
          setBomDetailLoading(true)

          const detail =
            await getProductionBomById(
              form.bom_id
            )

          if (!active) {
            return
          }

          setBomDetails(
            (previous) => ({
              ...previous,
              [String(form.bom_id)]:
                detail,
            })
          )
        } catch (error) {
          if (active) {
            notify(
              extractError(error),
              "error"
            )
          }
        } finally {
          if (active) {
            setBomDetailLoading(false)
          }
        }
      }

    loadSelectedBomDetail()

    return () => {
      active = false
    }
  }, [
    boms,
    bomDetails,
    form.bom_id,
    notify,
  ])


  const selectedBom =
    useMemo(() => {
      if (!selectedBomHeader) {
        return null
      }

      const detail =
        bomDetails[
          String(
            selectedBomHeader.id
          )
        ]

      if (!detail) {
        return selectedBomHeader
      }

      return {
        ...selectedBomHeader,
        ...detail,
        items:
          getBomItems(detail).length > 0
            ? getBomItems(detail)
            : getBomItems(
                selectedBomHeader
              ),
      }
    }, [
      selectedBomHeader,
      bomDetails,
    ])


  /* =======================================================
     CALCUL DES MATIÈRES
     ======================================================= */

  const calculatedMaterials =
    useMemo(() => {
      if (!selectedBom) {
        return []
      }

      const items =
        getBomItems(
          selectedBom
        )

      const bomOutput =
        getBomOutputQuantity(
          selectedBom
        )

      const planned =
        positiveNumber(
          form.planned_quantity,
          0
        )

      const factor =
        bomOutput > 0
          ? planned / bomOutput
          : 0

      return items.map(
        (item) => {
          const baseQuantity =
            toNumber(
              item.required_quantity ??
                item.quantity ??
                item.material_quantity ??
                item.qty_required,
              0
            )

          const wastePercent =
            toNumber(
              item.waste_percent ??
                item.loss_percent ??
                0,
              0
            )

          const quantityWithoutWaste =
            baseQuantity * factor

          const calculatedQuantity =
            quantityWithoutWaste *
            (1 +
              wastePercent / 100)

          return {
            ...item,

            base_quantity:
              baseQuantity,

            calculated_quantity:
              calculatedQuantity,

            calculated_waste_quantity:
              calculatedQuantity -
              quantityWithoutWaste,
          }
        }
      )
    }, [
      selectedBom,
      form.planned_quantity,
    ])


  /* =======================================================
     FILTRAGE
     ======================================================= */

  const filteredOrders =
    useMemo(() => {
      const term =
        search
          .trim()
          .toLowerCase()

      return orders.filter(
        (order) => {
          const haystack = [
            getOrderCode(order),

            order.bom_code,
            order.bom_name,

            getOrderProductCode(order),
            getOrderProductName(order),

            order.warehouse_code,
            order.warehouse_name,

            order.status,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()

          const matchesSearch =
            !term ||
            haystack.includes(term)

          const currentStatus =
            getOrderWorkflowState(
              order
            )

          let matchesStatus =
            true

          if (
            statusFilter !==
            "ALL"
          ) {
            if (
              statusFilter ===
              "IN_PROGRESS"
            ) {
              matchesStatus =
                [
                  "IN_PROGRESS",
                  "IN_PRODUCTION",
                  "STARTED",
                ].includes(
                  currentStatus
                )
            } else if (
              statusFilter ===
              "COMPLETED"
            ) {
              matchesStatus =
                [
                  "COMPLETED",
                  "FINISHED",
                  "DONE",
                ].includes(
                  currentStatus
                )
            } else if (
              statusFilter ===
              "RESERVED"
            ) {
              matchesStatus =
                currentStatus ===
                "MATERIALS_RESERVED"
            } else if (
              statusFilter ===
              "CANCELLED"
            ) {
              matchesStatus =
                [
                  "CANCELLED",
                  "CANCELED",
                ].includes(
                  currentStatus
                )
            } else if (
              statusFilter ===
              "DRAFT"
            ) {
              matchesStatus =
                currentStatus ===
                "TO_PREPARE"
            } else {
              matchesStatus =
                currentStatus ===
                statusFilter
            }
          }

          return (
            matchesSearch &&
            matchesStatus
          )
        }
      )
    }, [
      orders,
      search,
      statusFilter,
    ])


  /* =======================================================
     KPI
     ======================================================= */

  const dashboard =
    useMemo(() => {
      const result = {
        total: orders.length,
        draft: 0,
        reserved: 0,
        inProgress: 0,
        completed: 0,
      }

      orders.forEach(
        (order) => {
          const status =
            getOrderWorkflowState(
              order
            )

          if (
            status ===
            "TO_PREPARE"
          ) {
            result.draft += 1
          }

          if (
            status ===
            "MATERIALS_RESERVED"
          ) {
            result.reserved += 1
          }

          if (
            [
              "IN_PROGRESS",
              "IN_PRODUCTION",
              "STARTED",
            ].includes(status)
          ) {
            result.inProgress +=
              1
          }

          if (
            [
              "COMPLETED",
              "FINISHED",
              "DONE",
            ].includes(status)
          ) {
            result.completed +=
              1
          }
        }
      )

      return result
    }, [orders])


  /* =======================================================
     OUVERTURE CRÉATION
     ======================================================= */

  const openCreateDialog =
    () => {
      setForm({
        ...EMPTY_FORM,
      })

      setCreateOpen(true)
    }


  const closeCreateDialog =
    () => {
      if (saving) {
        return
      }

      setCreateOpen(false)

      setForm({
        ...EMPTY_FORM,
      })
    }


  /* =======================================================
     CHANGEMENT BOM
     ======================================================= */

  const handleBomChange =
    (value) => {
      const bom =
        boms.find(
          (item) =>
            Number(item.id) ===
            Number(value)
        )

      setForm(
        (previous) => ({
          ...previous,

          bom_id: value,

          planned_quantity:
            previous.planned_quantity ||
            getBomOutputQuantity(
              bom
            ),
        })
      )
    }


  /* =======================================================
     VALIDATION CRÉATION
     ======================================================= */

  const validateCreate =
    () => {
      if (!form.bom_id) {
        return "Sélectionnez une nomenclature de production."
      }

      if (
        !form.warehouse_id
      ) {
        return "Sélectionnez l'entrepôt de production."
      }

      if (
        positiveNumber(
          form.planned_quantity,
          0
        ) <= 0
      ) {
        return "La quantité à produire doit être supérieure à zéro."
      }

      if (
        form.planned_start_date &&
        form.planned_end_date
      ) {
        const start =
          new Date(
            `${form.planned_start_date}T00:00:00`
          )

        const end =
          new Date(
            `${form.planned_end_date}T00:00:00`
          )

        if (end < start) {
          return "La date de fin prévue ne peut pas être antérieure à la date de début."
        }
      }

      return ""
    }


  /* =======================================================
     CRÉATION
     ======================================================= */

  const handleCreate =
    async () => {
      const validation =
        validateCreate()

      if (validation) {
        notify(
          validation,
          "warning"
        )

        return
      }

      /*
       * IMPORTANT :
       *
       * On conserve volontairement le contrat backend
       * déjà utilisé par production.py :
       *
       * bom_id
       * warehouse_id
       * planned_quantity
       * planned_start_date
       * planned_end_date
       * notes
       *
       * Pas de renommage arbitraire ici.
       */

      const payload = {
        bom_id:
          Number(
            form.bom_id
          ),

        warehouse_id:
          Number(
            form.warehouse_id
          ),

        planned_quantity:
          toNumber(
            form.planned_quantity
          ),

        planned_start_date:
          form.planned_start_date ||
          null,

        planned_end_date:
          form.planned_end_date ||
          null,

        notes:
          form.notes?.trim() ||
          null,
      }

      try {
        setSaving(true)

        const result =
          await createProductionOrder(
            payload
          )

        setCreateOpen(false)

        setForm({
          ...EMPTY_FORM,
        })

        notify(
          result?.message ||
            "Ordre de production créé avec succès.",
          "success"
        )

        await loadData()
      } catch (error) {
        notify(
          extractError(error),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  /* =======================================================
     DÉTAIL
     ======================================================= */

  const openOrderDetail =
    async (orderId) => {
      setSelectedOrder(null)
      setDetailOpen(true)
      setDetailLoading(true)

      try {
        const data =
          await getProductionOrderById(
            orderId
          )

        setSelectedOrder(
          data
        )
      } catch (error) {
        setDetailOpen(false)

        notify(
          extractError(error),
          "error"
        )
      } finally {
        setDetailLoading(false)
      }
    }


  /* =======================================================
     RÉSERVATION MATIÈRES
     ======================================================= */

  const handleReserve =
    async (order) => {
      try {
        setSaving(true)

        const result =
          await reserveProductionMaterials(
            order.id
          )

        notify(
          result?.message ||
            "Matières premières réservées avec succès.",
          "success"
        )

        await loadData()
      } catch (error) {
        notify(
          extractError(error),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  /* =======================================================
     LANCEMENT
     ======================================================= */

  const handleStart =
    async (order) => {
      try {
        setSaving(true)

        const result =
          await startProductionOrder(
            order.id
          )

        notify(
          result?.message ||
            "Production lancée avec succès.",
          "success"
        )

        await loadData()
      } catch (error) {
        notify(
          extractError(error),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  /* =======================================================
     OUVERTURE TERMINAISON
     ======================================================= */

  const openCompletion =
    (order) => {
      setSelectedOrder(order)

      setCompletionForm({
        ...EMPTY_COMPLETION_FORM,

        produced_quantity:
          getOrderQuantity(
            order
          ),
      })

      setCompleteOpen(true)
    }


  /* =======================================================
     TERMINAISON
     ======================================================= */

  const handleComplete =
    async () => {
      if (!selectedOrder) {
        return
      }

      const produced =
        toNumber(
          completionForm
            .produced_quantity,
          0
        )

      const rejected =
        toNumber(
          completionForm
            .rejected_quantity,
          0
        )

      if (produced < 0) {
        notify(
          "La quantité produite ne peut pas être négative.",
          "warning"
        )

        return
      }

      if (rejected < 0) {
        notify(
          "La quantité rejetée ne peut pas être négative.",
          "warning"
        )

        return
      }

      if (
        produced +
          rejected <=
        0
      ) {
        notify(
          "Indiquez une quantité réellement produite ou rejetée.",
          "warning"
        )

        return
      }

      try {
        setSaving(true)

        const result =
          await completeProductionOrder(
            selectedOrder.id,
            {
              produced_quantity:
                produced,

              rejected_quantity:
                rejected,

              notes:
                completionForm
                  .notes
                  ?.trim() ||
                null,
            }
          )

        setCompleteOpen(false)

        setSelectedOrder(null)

        setCompletionForm({
          ...EMPTY_COMPLETION_FORM,
        })

        notify(
          result?.message ||
            "Production terminée. Le produit fini a été ajouté au stock.",
          "success"
        )

        await loadData()
      } catch (error) {
        notify(
          extractError(error),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  /* =======================================================
     ANNULATION
     ======================================================= */

  const handleCancel =
    async (order) => {
      const reason =
        window.prompt(
          `Motif d'annulation de l'ordre ${getOrderCode(
            order
          )} :`
        )

      if (reason === null) {
        return
      }

      try {
        setSaving(true)

        const result =
          await cancelProductionOrder(
            order.id,
            {
              reason:
                reason.trim() ||
                null,
            }
          )

        notify(
          result?.message ||
            "Ordre de production annulé.",
          "success"
        )

        await loadData()
      } catch (error) {
        notify(
          extractError(error),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  /* =======================================================
     ACTIONS
     ======================================================= */

  function renderActions(order) {
    const status =
      getOrderWorkflowState(
        order
      )

    const canReserve =
      status ===
      "TO_PREPARE"

    const canStart =
      status ===
      "MATERIALS_RESERVED"

    const canComplete =
      [
        "IN_PROGRESS",
        "IN_PRODUCTION",
        "STARTED",
      ].includes(status)

    const canCancel =
      ![
        "COMPLETED",
        "FINISHED",
        "DONE",
        "CANCELLED",
        "CANCELED",
      ].includes(status)

    return (
      <Stack
        direction="row"
        spacing={0.75}
        justifyContent="flex-end"
        flexWrap="wrap"
        useFlexGap
      >
        <Tooltip title="Afficher le détail">
          <Button
            size="small"
            variant="outlined"
            startIcon={
              <VisibilityOutlinedIcon />
            }
            onClick={() =>
              openOrderDetail(
                order.id
              )
            }
          >
            Voir
          </Button>
        </Tooltip>


        {canReserve && (
          <Button
            size="small"
            color="warning"
            variant="contained"
            startIcon={
              <Inventory2OutlinedIcon />
            }
            disabled={saving}
            onClick={() =>
              handleReserve(
                order
              )
            }
          >
            Réserver
          </Button>
        )}


        {canStart && (
          <Button
            size="small"
            color="primary"
            variant="contained"
            startIcon={
              <PlayArrowRoundedIcon />
            }
            disabled={saving}
            onClick={() =>
              handleStart(
                order
              )
            }
          >
            Lancer
          </Button>
        )}


        {canComplete && (
          <Button
            size="small"
            color="success"
            variant="contained"
            startIcon={
              <TaskAltRoundedIcon />
            }
            disabled={saving}
            onClick={() =>
              openCompletion(
                order
              )
            }
          >
            Terminer
          </Button>
        )}


        {canCancel && (
          <Button
            size="small"
            color="error"
            variant="outlined"
            disabled={saving}
            onClick={() =>
              handleCancel(
                order
              )
            }
          >
            Annuler
          </Button>
        )}
      </Stack>
    )
  }


  /* =======================================================
     RENDER
     ======================================================= */

  return (
    <MainLayout>
      <Box
        sx={{
          width: "100%",

          px: {
            xs: 1.5,
            sm: 2,
            md: 3,
          },

          py: {
            xs: 1.5,
            md: 2.5,
          },
        }}
      >

        {/* =================================================
            HEADER
            ================================================= */}

        <Stack
          direction={{
            xs: "column",
            lg: "row",
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "stretch",
            lg: "center",
          }}
          spacing={2}
          mb={1.5}
        >
          <Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <FactoryOutlinedIcon
                color="primary"
              />

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,

                  fontSize: {
                    xs: 25,
                    md: 31,
                  },
                }}
              >
                Ordres de production
              </Typography>
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.4,
              }}
            >
              Planification,
              réservation des matières,
              exécution et déclaration
              des produits finis.
            </Typography>
          </Box>


          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1}
          >
            <Button
              variant="outlined"
              startIcon={
                <RefreshRoundedIcon />
              }
              onClick={loadData}
              disabled={
                loading ||
                saving
              }
            >
              Actualiser
            </Button>

            <Button
              variant="contained"
              startIcon={
                <AddRoundedIcon />
              }
              onClick={
                openCreateDialog
              }
              disabled={loading}
            >
              Nouvel ordre
            </Button>
          </Stack>
        </Stack>


        {/* =================================================
            WORKFLOW
            ================================================= */}

        <Alert
          severity="info"
          sx={{
            mb: 2,
          }}
        >
          <strong>
            Production :
          </strong>{" "}
          Nomenclature (BOM) → Ordre de
          production → Réservation des
          matières → Lancement →
          Production réelle → Sortie des
          matières consommées → Entrée
          automatique du produit fini en
          stock.
        </Alert>


        {/* =================================================
            KPI
            ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(5, minmax(0, 1fr))",
            },

            gap: 1.5,
            mb: 2,
          }}
        >
          {[
            {
              label: "Ordres",
              value:
                dashboard.total,
            },

            {
              label: "À préparer",
              value:
                dashboard.draft,
            },

            {
              label:
                "Matières réservées",
              value:
                dashboard.reserved,
            },

            {
              label:
                "En production",
              value:
                dashboard.inProgress,
            },

            {
              label: "Terminés",
              value:
                dashboard.completed,
            },
          ].map(
            (item) => (
              <Paper
                key={item.label}
                variant="outlined"
                sx={{
                  p: 2,
                  minHeight: 94,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {item.label}
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mt: 0.5,
                  }}
                >
                  {item.value}
                </Typography>
              </Paper>
            )
          )}
        </Box>


        {/* =================================================
            FILTRE
            ================================================= */}

        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            mb: 2,
          }}
        >
          <Stack
            direction={{
              xs: "column",
              md: "row",
            }}
            spacing={1.5}
          >
            <TextField
              fullWidth
              placeholder="Rechercher ordre, produit, nomenclature, entrepôt..."
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              InputProps={{
                startAdornment: (
                  <SearchRoundedIcon
                    sx={{
                      mr: 1,
                      color:
                        "text.secondary",
                    }}
                  />
                ),
              }}
            />


            <FormControl
              sx={{
                minWidth: {
                  xs: "100%",
                  md: 220,
                },
              }}
            >
              <InputLabel>
                Statut
              </InputLabel>

              <Select
                value={
                  statusFilter
                }
                label="Statut"
                onChange={(event) =>
                  setStatusFilter(
                    event.target.value
                  )
                }
              >
                <MenuItem value="ALL">
                  Tous
                </MenuItem>

                <MenuItem value="DRAFT">
                  À préparer
                </MenuItem>

                <MenuItem value="RESERVED">
                  Matières réservées
                </MenuItem>

                <MenuItem value="IN_PROGRESS">
                  En production
                </MenuItem>

                <MenuItem value="COMPLETED">
                  Terminés
                </MenuItem>

                <MenuItem value="CANCELLED">
                  Annulés
                </MenuItem>
              </Select>
            </FormControl>


            <Button
              variant="outlined"
              onClick={() => {
                setSearch("")
                setStatusFilter(
                  "ALL"
                )
              }}
            >
              Reset
            </Button>
          </Stack>
        </Paper>


        {/* =================================================
            TABLEAU
            ================================================= */}

        <Paper
          variant="outlined"
          sx={{
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 2,
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
            >
              Ordres de fabrication
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Suivi complet depuis la
              planification jusqu'à
              l'entrée du produit fini en
              stock.
            </Typography>
          </Box>

          <Divider />

          {loading ? (
            <Box
              sx={{
                py: 8,
                display: "flex",
                justifyContent:
                  "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table
                size="small"
                sx={{
                  minWidth: 1280,
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>
                      Ordre
                    </TableCell>

                    <TableCell>
                      Produit fini
                    </TableCell>

                    <TableCell>
                      BOM
                    </TableCell>

                    <TableCell>
                      Entrepôt
                    </TableCell>

                    <TableCell align="right">
                      Qté prévue
                    </TableCell>

                    <TableCell>
                      Début
                    </TableCell>

                    <TableCell>
                      Fin prévue
                    </TableCell>

                    <TableCell>
                      Statut
                    </TableCell>

                    <TableCell align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>


                <TableBody>
                  {filteredOrders.length ===
                  0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        align="center"
                        sx={{
                          py: 8,
                        }}
                      >
                        <FactoryOutlinedIcon
                          sx={{
                            fontSize: 44,
                            color:
                              "text.disabled",
                            mb: 1,
                          }}
                        />

                        <Typography
                          fontWeight={600}
                        >
                          Aucun ordre de
                          production.
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          Créez un ordre à
                          partir d'une
                          nomenclature active.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredOrders.map(
                      (order) => {
                        const status =
                          statusInfo(
                            order.status,
                            order
                          )

                        return (
                          <TableRow
                            key={order.id}
                            hover
                          >
                            <TableCell>
                              <Typography
                                fontWeight={
                                  700
                                }
                              >
                                {getOrderCode(
                                  order
                                )}
                              </Typography>
                            </TableCell>


                            <TableCell>
                              <Typography
                                fontWeight={
                                  600
                                }
                              >
                                {getOrderProductName(
                                  order
                                )}
                              </Typography>

                              {getOrderProductCode(
                                order
                              ) && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {getOrderProductCode(
                                    order
                                  )}
                                </Typography>
                              )}
                            </TableCell>


                            <TableCell>
                              <Typography
                                fontWeight={
                                  500
                                }
                              >
                                {order.bom_code ||
                                  "-"}
                              </Typography>

                              {order.bom_version && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Version{" "}
                                  {
                                    order.bom_version
                                  }
                                </Typography>
                              )}
                            </TableCell>


                            <TableCell>
                              {getOrderWarehouseName(
                                order
                              ) || "-"}
                            </TableCell>


                            <TableCell align="right">
                              <Typography
                                fontWeight={
                                  600
                                }
                              >
                                {formatQuantity(
                                  getOrderQuantity(
                                    order
                                  ),
                                  getOrderUnit(
                                    order
                                  )
                                )}
                              </Typography>
                            </TableCell>


                            <TableCell>
                              {formatDate(
                                order.planned_start_date ??
                                  order.start_date
                              )}
                            </TableCell>


                            <TableCell>
                              {formatDate(
                                order.planned_end_date ??
                                  order.end_date
                              )}
                            </TableCell>


                            <TableCell>
                              <Chip
                                size="small"
                                color={
                                  status.color
                                }
                                label={
                                  status.label
                                }
                              />
                            </TableCell>


                            <TableCell align="right">
                              {renderActions(
                                order
                              )}
                            </TableCell>
                          </TableRow>
                        )
                      }
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>


        {/* =================================================
            CRÉATION ORDRE
            ================================================= */}

        <Dialog
          open={createOpen}
          onClose={
            closeCreateDialog
          }
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle>
            Nouvel ordre de production
          </DialogTitle>

          <DialogContent dividers>
            <Alert
              severity="info"
              sx={{
                mb: 2,
              }}
            >
              Sélectionnez une
              nomenclature active, la
              quantité à fabriquer et
              l'entrepôt concerné. RLSys
              calculera automatiquement
              les besoins en matières
              premières.
            </Alert>


            {/* ================================
                PARAMÈTRES PRINCIPAUX
                ================================ */}

            <Box
              sx={{
                display: "grid",

                gridTemplateColumns: {
                  xs: "1fr",
                  md: "2fr 1fr",
                },

                gap: 2,
              }}
            >
              <FormControl
                fullWidth
                required
              >
                <InputLabel>
                  Nomenclature
                </InputLabel>

                <Select
                  label="Nomenclature"
                  value={
                    form.bom_id
                  }
                  onChange={(event) =>
                    handleBomChange(
                      event.target.value
                    )
                  }
                >
                  {boms.length ===
                  0 && (
                    <MenuItem
                      disabled
                      value=""
                    >
                      Aucune nomenclature
                      active
                    </MenuItem>
                  )}

                  {boms.map(
                    (bom) => (
                      <MenuItem
                        key={bom.id}
                        value={bom.id}
                      >
                        {bomLabel(
                          bom
                        )}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>


              <TextField
                required
                type="number"
                label="Quantité à produire"
                value={
                  form.planned_quantity
                }
                inputProps={{
                  min: 0.0001,
                  step: "any",
                }}
                onChange={(event) =>
                  setForm(
                    (previous) => ({
                      ...previous,

                      planned_quantity:
                        event.target
                          .value,
                    })
                  )
                }
              />


              <FormControl
                fullWidth
                required
              >
                <InputLabel>
                  Entrepôt de production
                </InputLabel>

                <Select
                  label="Entrepôt de production"
                  value={
                    form.warehouse_id
                  }
                  onChange={(event) =>
                    setForm(
                      (previous) => ({
                        ...previous,

                        warehouse_id:
                          event.target
                            .value,
                      })
                    )
                  }
                >
                  {warehouses.length ===
                  0 && (
                    <MenuItem
                      disabled
                      value=""
                    >
                      Aucun entrepôt
                      disponible
                    </MenuItem>
                  )}

                  {warehouses.map(
                    (warehouse) => (
                      <MenuItem
                        key={
                          warehouse.id
                        }
                        value={
                          warehouse.id
                        }
                      >
                        {warehouseLabel(
                          warehouse
                        )}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>


              {selectedBom ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Produit à fabriquer
                  </Typography>

                  <Typography
                    fontWeight={700}
                  >
                    {[
                      getBomProductCode(
                        selectedBom
                      ),

                      getBomProductName(
                        selectedBom
                      ),
                    ]
                      .filter(Boolean)
                      .join(" - ") ||
                      "-"}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.5,
                    }}
                  >
                    Base BOM :{" "}
                    {formatQuantity(
                      getBomOutputQuantity(
                        selectedBom
                      ),
                      getBomOutputUnit(
                        selectedBom
                      )
                    )}
                  </Typography>
                </Paper>
              ) : (
                <Box />
              )}


              <TextField
                type="date"
                label="Début prévu"
                value={
                  form.planned_start_date
                }
                InputLabelProps={{
                  shrink: true,
                }}
                onChange={(event) =>
                  setForm(
                    (previous) => ({
                      ...previous,

                      planned_start_date:
                        event.target
                          .value,
                    })
                  )
                }
              />


              <TextField
                type="date"
                label="Fin prévue"
                value={
                  form.planned_end_date
                }
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min:
                    form.planned_start_date ||
                    undefined,
                }}
                onChange={(event) =>
                  setForm(
                    (previous) => ({
                      ...previous,

                      planned_end_date:
                        event.target
                          .value,
                    })
                  )
                }
              />
            </Box>


            {/* ================================
                BESOINS MATIÈRES
                ================================ */}

            {selectedBom && (
              <>
                <Divider
                  sx={{
                    my: 3,
                  }}
                />

                <Stack
                  direction={{
                    xs: "column",
                    sm: "row",
                  }}
                  justifyContent="space-between"
                  spacing={1}
                  mb={1.5}
                >
                  <Box>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                    >
                      Besoins calculés en
                      matières premières
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Quantités théoriques
                      requises pour fabriquer{" "}
                      {formatQuantity(
                        form.planned_quantity,
                        getBomOutputUnit(
                          selectedBom
                        )
                      )}
                      .
                    </Typography>
                  </Box>

                  <Chip
                    color="primary"
                    variant="outlined"
                    label={
                      bomDetailLoading
                        ? "Chargement..."
                        : `${calculatedMaterials.length} composant(s)`
                    }
                  />
                </Stack>

                {bomDetailLoading && (
                  <Alert
                    severity="info"
                    sx={{ mb: 1.5 }}
                  >
                    Chargement des composants de la nomenclature...
                  </Alert>
                )}

                <TableContainer
                  component={Paper}
                  variant="outlined"
                >
                  <Table
                    size="small"
                    sx={{
                      minWidth: 650,
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          Matière première
                        </TableCell>

                        <TableCell align="right">
                          Besoin BOM
                        </TableCell>

                        <TableCell align="right">
                          Besoin calculé
                        </TableCell>

                        <TableCell>
                          Unité
                        </TableCell>

                        <TableCell align="right">
                          Perte %
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {calculatedMaterials.length ===
                      0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            align="center"
                            sx={{
                              py: 4,
                            }}
                          >
                            Aucun composant
                            disponible dans
                            cette nomenclature.
                          </TableCell>
                        </TableRow>
                      ) : (
                        calculatedMaterials.map(
                          (
                            item,
                            index
                          ) => {
                            const code =
                              item.material_product_code ||
                              item.product_code ||
                              ""

                            const name =
                              item.material_product_name ||
                              item.product_name ||
                              item.material_name ||
                              ""

                            const unit =
                              item.unit ||
                              item.material_unit ||
                              ""

                            return (
                              <TableRow
                                key={
                                  item.id ||
                                  index
                                }
                              >
                                <TableCell>
                                  <Typography
                                    fontWeight={
                                      600
                                    }
                                  >
                                    {[
                                      code,
                                      name,
                                    ]
                                      .filter(
                                        Boolean
                                      )
                                      .join(
                                        " - "
                                      ) ||
                                      "-"}
                                  </Typography>
                                </TableCell>

                                <TableCell align="right">
                                  {formatQuantity(
                                    item.base_quantity,
                                    unit
                                  )}
                                </TableCell>

                                <TableCell align="right">
                                  <Typography
                                    fontWeight={
                                      700
                                    }
                                  >
                                    {formatQuantity(
                                      item.calculated_quantity,
                                      unit
                                    )}
                                  </Typography>
                                </TableCell>

                                <TableCell>
                                  {unit ||
                                    "-"}
                                </TableCell>

                                <TableCell align="right">
                                  {toNumber(
                                    item.waste_percent ??
                                      item.loss_percent
                                  ).toLocaleString(
                                    "fr-FR",
                                    {
                                      maximumFractionDigits: 2,
                                    }
                                  )}
                                  %
                                </TableCell>
                              </TableRow>
                            )
                          }
                        )
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}


            {/* ================================
                NOTES
                ================================ */}

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Notes / instructions de production"
              value={form.notes}
              onChange={(event) =>
                setForm(
                  (previous) => ({
                    ...previous,

                    notes:
                      event.target.value,
                  })
                )
              }
              sx={{
                mt: 3,
              }}
            />
          </DialogContent>


          <DialogActions
            sx={{
              px: 3,
              py: 2,
            }}
          >
            <Button
              onClick={
                closeCreateDialog
              }
              disabled={saving}
            >
              Annuler
            </Button>

            <Button
              variant="contained"
              startIcon={
                saving ? (
                  <CircularProgress
                    size={18}
                    color="inherit"
                  />
                ) : (
                  <FactoryOutlinedIcon />
                )
              }
              onClick={
                handleCreate
              }
              disabled={
                saving ||
                boms.length === 0 ||
                warehouses.length ===
                  0
              }
            >
              {saving
                ? "Création..."
                : "Créer l'ordre"}
            </Button>
          </DialogActions>
        </Dialog>


        {/* =================================================
            DÉTAIL ORDRE
            ================================================= */}

        <Dialog
          open={detailOpen}
          onClose={() =>
            !detailLoading &&
            setDetailOpen(false)
          }
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle>
            Détail de l'ordre de
            production
          </DialogTitle>

          <DialogContent dividers>
            {detailLoading ? (
              <Box
                sx={{
                  py: 8,
                  display: "flex",
                  justifyContent:
                    "center",
                }}
              >
                <CircularProgress />
              </Box>
            ) : !selectedOrder ? (
              <Alert severity="warning">
                Détail de l'ordre
                indisponible.
              </Alert>
            ) : (
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "grid",

                    gridTemplateColumns:
                      {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        lg: "repeat(4, 1fr)",
                      },

                    gap: 1.5,
                  }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      Ordre
                    </Typography>

                    <Typography
                      fontWeight={700}
                    >
                      {getOrderCode(
                        selectedOrder
                      )}
                    </Typography>
                  </Paper>


                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      Produit fini
                    </Typography>

                    <Typography
                      fontWeight={700}
                    >
                      {getOrderProductName(
                        selectedOrder
                      )}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      {getOrderProductCode(
                        selectedOrder
                      )}
                    </Typography>
                  </Paper>


                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      Quantité prévue
                    </Typography>

                    <Typography
                      fontWeight={700}
                    >
                      {formatQuantity(
                        getOrderQuantity(
                          selectedOrder
                        ),
                        getOrderUnit(
                          selectedOrder
                        )
                      )}
                    </Typography>
                  </Paper>


                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      Statut
                    </Typography>

                    <Box
                      sx={{
                        mt: 0.5,
                      }}
                    >
                      <Chip
                        size="small"
                        color={
                          statusInfo(
                            selectedOrder.status,
                            selectedOrder
                          ).color
                        }
                        label={
                          statusInfo(
                            selectedOrder.status,
                            selectedOrder
                          ).label
                        }
                      />
                    </Box>
                  </Paper>
                </Box>


                <Box
                  sx={{
                    display: "grid",

                    gridTemplateColumns:
                      {
                        xs: "1fr",
                        md: "repeat(3, 1fr)",
                      },

                    gap: 1.5,
                  }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      Entrepôt
                    </Typography>

                    <Typography>
                      {getOrderWarehouseName(
                        selectedOrder
                      ) || "-"}
                    </Typography>
                  </Paper>


                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      Début prévu
                    </Typography>

                    <Typography>
                      {formatDate(
                        selectedOrder.planned_start_date ??
                          selectedOrder.start_date
                      )}
                    </Typography>
                  </Paper>


                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      Fin prévue
                    </Typography>

                    <Typography>
                      {formatDate(
                        selectedOrder.planned_end_date ??
                          selectedOrder.end_date
                      )}
                    </Typography>
                  </Paper>
                </Box>


                <Divider />


                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                  >
                    Matières de production
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Besoins, réservations
                    et consommation des
                    matières premières.
                  </Typography>
                </Box>


                <TableContainer
                  component={Paper}
                  variant="outlined"
                >
                  <Table
                    size="small"
                    sx={{
                      minWidth: 750,
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          Matière
                        </TableCell>

                        <TableCell align="right">
                          Besoin
                        </TableCell>

                        <TableCell align="right">
                          Réservé
                        </TableCell>

                        <TableCell align="right">
                          Consommé
                        </TableCell>

                        <TableCell>
                          Unité
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {normalizeProductionArray(
                        selectedOrder.materials ||
                          selectedOrder.items ||
                          selectedOrder.production_materials
                      ).length ===
                      0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            align="center"
                            sx={{
                              py: 4,
                            }}
                          >
                            Aucune matière
                            disponible.
                          </TableCell>
                        </TableRow>
                      ) : (
                        normalizeProductionArray(
                          selectedOrder.materials ||
                            selectedOrder.items ||
                            selectedOrder.production_materials
                        ).map(
                          (
                            item,
                            index
                          ) => {
                            const unit =
                              item.unit ||
                              item.material_unit ||
                              ""

                            return (
                              <TableRow
                                key={
                                  item.id ||
                                  index
                                }
                              >
                                <TableCell>
                                  <Typography
                                    fontWeight={
                                      600
                                    }
                                  >
                                    {[
                                      item.material_product_code ||
                                        item.product_code,

                                      item.material_product_name ||
                                        item.product_name ||
                                        item.material_name,
                                    ]
                                      .filter(
                                        Boolean
                                      )
                                      .join(
                                        " - "
                                      ) ||
                                      "-"}
                                  </Typography>
                                </TableCell>

                                <TableCell align="right">
                                  {formatQuantity(
                                    item.required_quantity ??
                                      item.planned_quantity,
                                    unit
                                  )}
                                </TableCell>

                                <TableCell align="right">
                                  {formatQuantity(
                                    item.reserved_quantity,
                                    unit
                                  )}
                                </TableCell>

                                <TableCell align="right">
                                  {formatQuantity(
                                    item.consumed_quantity,
                                    unit
                                  )}
                                </TableCell>

                                <TableCell>
                                  {unit ||
                                    "-"}
                                </TableCell>
                              </TableRow>
                            )
                          }
                        )
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>


                {(selectedOrder.notes ||
                  selectedOrder.created_at ||
                  selectedOrder.started_at ||
                  selectedOrder.completed_at) && (
                  <>
                    <Divider />

                    <Box
                      sx={{
                        display: "grid",

                        gridTemplateColumns:
                          {
                            xs: "1fr",
                            md: "repeat(3, 1fr)",
                          },

                        gap: 1.5,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          Créé le
                        </Typography>

                        <Typography>
                          {formatDateTime(
                            selectedOrder.created_at
                          )}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          Lancé le
                        </Typography>

                        <Typography>
                          {formatDateTime(
                            selectedOrder.started_at
                          )}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          Terminé le
                        </Typography>

                        <Typography>
                          {formatDateTime(
                            selectedOrder.completed_at
                          )}
                        </Typography>
                      </Box>
                    </Box>

                    {selectedOrder.notes && (
                      <Alert
                        severity="info"
                        icon={false}
                      >
                        <strong>
                          Notes :
                        </strong>{" "}
                        {
                          selectedOrder.notes
                        }
                      </Alert>
                    )}
                  </>
                )}
              </Stack>
            )}
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() =>
                setDetailOpen(
                  false
                )
              }
            >
              Fermer
            </Button>
          </DialogActions>
        </Dialog>


        {/* =================================================
            TERMINER PRODUCTION
            ================================================= */}

        <Dialog
          open={completeOpen}
          onClose={() =>
            !saving &&
            setCompleteOpen(false)
          }
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            Terminer la production
          </DialogTitle>

          <DialogContent dividers>
            <Alert
              severity="success"
              sx={{
                mb: 2,
              }}
            >
              Cette opération finalise
              l'ordre. Les matières
              consommées seront
              comptabilisées et la
              quantité réellement produite
              sera ajoutée au stock de
              produits finis.
            </Alert>


            {selectedOrder && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 2,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Ordre
                </Typography>

                <Typography
                  fontWeight={700}
                >
                  {getOrderCode(
                    selectedOrder
                  )}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.5,
                  }}
                >
                  {getOrderProductName(
                    selectedOrder
                  )}{" "}
                  • prévu :{" "}
                  <strong>
                    {formatQuantity(
                      getOrderQuantity(
                        selectedOrder
                      ),
                      getOrderUnit(
                        selectedOrder
                      )
                    )}
                  </strong>
                </Typography>
              </Paper>
            )}


            <Stack spacing={2}>
              <TextField
                required
                type="number"
                label="Quantité réellement produite"
                value={
                  completionForm
                    .produced_quantity
                }
                inputProps={{
                  min: 0,
                  step: "any",
                }}
                onChange={(event) =>
                  setCompletionForm(
                    (previous) => ({
                      ...previous,

                      produced_quantity:
                        event.target.value,
                    })
                  )
                }
              />


              <TextField
                type="number"
                label="Quantité rejetée / rebut"
                value={
                  completionForm
                    .rejected_quantity
                }
                inputProps={{
                  min: 0,
                  step: "any",
                }}
                onChange={(event) =>
                  setCompletionForm(
                    (previous) => ({
                      ...previous,

                      rejected_quantity:
                        event.target.value,
                    })
                  )
                }
              />


              <TextField
                multiline
                minRows={3}
                label="Observations de production"
                value={
                  completionForm.notes
                }
                onChange={(event) =>
                  setCompletionForm(
                    (previous) => ({
                      ...previous,

                      notes:
                        event.target.value,
                    })
                  )
                }
              />
            </Stack>
          </DialogContent>


          <DialogActions
            sx={{
              p: 2,
            }}
          >
            <Button
              onClick={() =>
                setCompleteOpen(
                  false
                )
              }
              disabled={saving}
            >
              Annuler
            </Button>

            <Button
              variant="contained"
              color="success"
              startIcon={
                saving ? (
                  <CircularProgress
                    size={18}
                    color="inherit"
                  />
                ) : (
                  <TaskAltRoundedIcon />
                )
              }
              onClick={
                handleComplete
              }
              disabled={saving}
            >
              {saving
                ? "Validation..."
                : "Terminer et mettre en stock"}
            </Button>
          </DialogActions>
        </Dialog>


        {/* =================================================
            SNACKBAR
            ================================================= */}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() =>
            setSnackbar(
              (previous) => ({
                ...previous,
                open: false,
              })
            )
          }
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <Alert
            severity={
              snackbar.severity
            }
            variant="filled"
            onClose={() =>
              setSnackbar(
                (previous) => ({
                  ...previous,
                  open: false,
                })
              )
            }
            sx={{
              width: "100%",
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </MainLayout>
  )
}